import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import '../styles/Home.css';
import aboutImage1 from '../images/about-nutrition1.jpg';
import aboutImage2 from '../images/about-nutrition2.jpg';

// --- Feature Data ---
const features = [
  {
    title: "Weekly Diet Plan",
    description: "Receive and adjust your personalized weekly meal plans directly from your dietitian.",
    icon: "M13 14h-2V9h2v5zm4 0h-2V9h2v5zm4 0h-2V9h2v5zm4 0h-2V9h2v5z"
  },
  {
    title: "Weekly Report Summary",
    description: "Get concise, easy-to-digest summaries of your nutritional intake and goal progress.",
    icon: "M10 20H4V4h6v16zm10 0h-6V4h6v16z"
  },
  {
    title: "Relaxing De-stressing Game",
    description: "Take a break with our calming game designed to reduce stress and improve focus.",
    icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15H9v-2h2v2zm0-4H9v-2h2v2zm0-4H9V7h2v2zm4 8h-2v-2h2v2zm0-4h-2v-2h2v2zm0-4h-2V7h2v2z"
  },
  {
    title: "Customised Recipes Specially Curated",
    description: "Explore hundreds of healthy, dietitian-approved recipes tailored to your dietary needs.",
    icon: "M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z"
  },
  {
    title: "Health Blogs & Articles",
    description: "Stay informed with the latest nutrition science and wellness tips from experts.",
    icon: "M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-1 16H6V6h12v13z"
  },
  {
    title: "One-on-One Interaction",
    description: "Direct messaging and virtual sessions with your personal, certified dietitian.",
    icon: "M15 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm-9-2c1.38 0 2.5-1.12 2.5-2.5S7.38 5 6 5 3.5 6.12 3.5 7.5 4.62 10 6 10zm-2 9c0 1.71 4.3 2.5 6 2.5s6-.79 6-2.5V17H4v2z"
  }
];
const pillarsGroupedData = [
  {
    mainPillar: {
      id: 'p1', type: 'main-pillar', 
      title: "SIMPLICITY", 
      bgColor: "#50c878", // Using the bright orange from your image
      textColor: "white"
    },
    descriptionPillar: {
      id: 'd1', type: 'description', 
      content: "Health should be effortless. No noise, just clear insights.", 
      bgColor: "e0f2e5", // Light green accent
      textColor: "var(--color-dark-text)"
    }
  },
  {
    mainPillar: {
      id: 'p2', type: 'main-pillar', 
      title: "BALANCE", 
      bgColor: "#ffcc00", // Feature Highlight Gold
      textColor: "white"
    },
    descriptionPillar: {
      id: 'd2', type: 'description', 
      content: "Sustainable habits, not restrictive diets. Food is fuel, not a foe.", 
      bgColor: "#fff9e5", // Light gold accent
      textColor: "white"
    }
  },
  {
    mainPillar: {
      id: 'p3', type: 'main-pillar', 
      title: "CLARITY", 
      bgColor: "#114e44", // Highlight Red
      textColor: "white"
    },
    descriptionPillar: {
      id: 'd3', type: 'description', 
      content: "Visualize progress with data that makes sense. Know *why* you're changing.", 
      bgColor: "#fce0e0", // Light red accent
      textColor: "var(--color-dark-text)"
    }
  },
  {
    mainPillar: {
      id: 'p4', type: 'main-pillar', 
      title: "GUIDANCE", 
      bgColor: "#1c1c1c", // Dark for "GUIDANCE"
      textColor: "white"
    },
    descriptionPillar: {
      id: 'd4', type: 'description', 
      content: "Connect with certified dietitians. Expert support is a click away.", 
      bgColor: "#e9e9e9", // Light gray accent
      textColor: "var(--color-dark-text)"
    }
  }
];

// --- Feature Carousel Component ---
const FeatureCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % features.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const currentFeature = features[currentIndex];

  const LightningIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" className="lightning-icon">
      <path d="M11 21v-4h-2L13 3v8h3l-5 10z" />
    </svg>
  );

  return (
    <section id="features" className="feature-carousel-section">
      <div className="carousel-container" style={{ transform: `translateX(-${currentIndex * 100}%)` }}>
        {features.map((feature, index) => (
          <div key={index} className={`feature-card-slide ${index === currentIndex ? 'active' : ''}`}>
            <div className="card-content-wrapper">
              <h2 className="feature-title">{feature.title}</h2>
              <p className="feature-description">{feature.description}</p>
              <p className="Feature-icon-svg"></p>
              <div className="feature-graphic-zone">
                <LightningIcon />
                <div className="graphic-detail detail-1">
                  ⚡ {feature.title.split(' ')[0]}
                </div>
                <div className="graphic-detail detail-2">⏱️ Everything At Your Time</div>
                <div className="graphic-detail detail-3">📈 {feature.title}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="carousel-pagination">
        {features.map((_, index) => (
          <button
            key={index}
            className={`dot ${index === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(index)}
            aria-label={`Go to feature ${index + 1}`}
          ></button>
        ))}
      </div>
    </section>
  );
};

// --- New Component: HowItWorksSection ---
const HowItWorksSection = () => {
    // Data for the 3 steps
    const processSteps = [
        {
            title: "Log Your Intake",
            description: "Effortlessly log meals, activity, and mood using simple text or photos. Our system handles the complex tracking.",
            icon: "📝"
        },
        {
            title: "Get Clarity & Learn",
            description: "Receive concise Weekly Report Summaries and insights, allowing you to visualize and understand your nutritional trends.",
            icon: "💡"
        },
        {
            title: "Curated Plan & Support",
            description: "Get a personalized weekly plan and connect directly with your certified dietitian for one-on-one expert guidance.",
            icon: "✅"
        }
    ];

    return (
        <section id="how-it-works" className="how-it-works-dark-geometric">
            <div className="background-title-how-it-works">PROCESS</div>

            <h2 className="section-title-how-it-works">
                How It Works
            </h2>
            
            <div className="steps-container-geometric">
                {/* REMOVED: Decorative Glowing Icons/Emodjis */}

                {processSteps.map((step, index) => (
                    <div key={index} className={`step-item step-${index + 1}`}>
                        
                        {/* NEW: Horizontal Bar Placeholder (Visible only on Desktop) */}
                        <div className="horizontal-bar-filler">
                            {/* Inner glow or text can go here if needed later */}
                            <div className="inner-bar-glow"></div>
                            <div className="bar-progress-indicator"></div>
                        </div>

                        <div className="step-shape-outline">
                            <div className="step-shape-inner">
                                <span className="step-icon-emoji">
                                    {index === 0 && '📄'}
                                    {index === 1 && '💡'}
                                    {index === 2 && '✅'}
                                </span>
                            </div>
                        </div>
                        
                        <div className="step-content">
                            <h3 className="step-content-title">
                                <span className="title-star-icon">✨</span> {step.title}
                            </h3>
                            <p className="step-content-description">{step.description}</p>
                        </div>
                    </div>
                ))}
            </div>
        </section>
    );
};
// --- Minimal Header ---
const PageHeaderMinimal = () => (
  <header className="page-header-minimal">
    <div className="logo-text">NUTRITION TRENDS.</div>
  </header>
);

// --- About Section (Modified for Clean Theme) ---
const AboutSection = () => (
  <section id="about" className="about-section-editorial">
    <div className="about-editorial-overlay">
      <div className="about-editorial-content">
        <h2 className="editorial-title">
          THE pursuit THAT SHAPED <span className="title-highlight">balance</span> AND
          <br />
          <span className="title-highlight">PHILOSOPHY</span>
        </h2>

        <div className="editorial-description-box">
          <p>
            We believe the path to health lies in simplicity and balance. <b>Nutrition Trends</b> was
            founded on the philosophy that true well-being is not a trend, but a daily habit built on
            clarity and sustainable action.</p> <p>We provide the tools and expert guidance to reshape your
            relationship with food, making health an effortless component of your everyday life.
          </p>
        </div>
      </div>
    </div>

    <img
      src={aboutImage1}
      alt="A striking visual representing focus and balanced living."
      className="about-editorial-background"
    />
  </section>
);
// ... (The imports and other components remain unchanged)

// --- PillarsGrid Component (FINAL MODIFICATION) ---
// --- PillarsGrid Component (FINAL VERTICAL STACK) ---
const PillarsGrid = () => {
  const [openPillarId, setOpenPillarId] = useState(null);
  const handlePillarClick = (id) => {
        // Simple check to match the CSS media query condition: 
        // We only want to enable this toggle logic on small screens.
        // For a true React solution, you'd use a custom hook to check window size, 
        // but for this implementation, we assume the CSS handles the mobile display.
        
        // Toggle logic: If the clicked pillar is already open, close it (set to null).
        // Otherwise, open the clicked pillar.
        if (id === openPillarId) {
            setOpenPillarId(null);
        } else {
            setOpenPillarId(id);
        }
    };
  return (
        <section className="pillars-section-vertical">
            <h2 className="pillars-main-title-complex">OUR FOUR CORE PILLARS</h2>
            {/* NEW grid class for the 4-column stack */}
            <div className="pillar-cards-container vertical-stack-grid"> 
                {pillarsGroupedData.map((group) => {
                    const isPillarOpen = group.mainPillar.id === openPillarId;

                    return (
                        <div 
                            key={group.mainPillar.id} 
                            className={`pillar-card-vertical ${isPillarOpen ? 'show-description-mobile' : ''}`} 
                            style={{ 
                                backgroundColor: group.mainPillar.bgColor,
                                color: group.mainPillar.textColor,
                            }}
                            data-pillar-id={group.mainPillar.id}
                            // Add the onClick handler
                            onClick={() => handlePillarClick(group.mainPillar.id)}
                        >
                            {/* Wrapper for vertical text */}
                            <div className="vertical-content-wrapper">
                                <h3 className="pillar-title-vertical">
                                    {group.mainPillar.title}
                                </h3>
                                <p className="pillar-description-vertical">
                                    {group.descriptionPillar.content.split('.')[0]}.
                                </p>
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
};
// ... (The rest of the HomePage component remains unchanged)

// --- NEW Structured Footer Component ---
const StructuredFooter = () => {
  return (
    <footer className="footer-structured-dark">
      <div className="footer-content-grid">
        {/* Column 1: Main Navigation */}
        <div className="footer-column navigation-column">
          <h4 className="column-title">PAGES</h4>
          <ul>
            <li><Link to="/">HOME</Link></li>
            <li><Link to="/about">ABOUT</Link></li>
            <li><Link to="/contact">CONTACT</Link></li>
            <li><Link to="/recipes">RECIPES</Link></li>
            <li><Link to="/plans">PLANS</Link></li>
          </ul>
        </div>

        {/* Column 2: Resources & Legal */}
        <div className="footer-column resources-column">
          <h4 className="column-title">RESOURCES</h4>
          <ul>
            <li><Link to="/privacy">PRIVACY POLICY</Link></li>
            <li><Link to="/terms">TERMS OF USE</Link></li>
            <li><Link to="/faq">FAQ</Link></li>
            <li><Link to="/careers">CAREERS</Link></li>
            <li><Link to="/blogs">BLOGS</Link></li>
          </ul>
        </div>

        {/* Column 3: Support Hours (Adapted from "Opening Hours") */}
        <div className="footer-column hours-column">
          <h4 className="column-title">SUPPORT HOURS</h4>
          <div className="hour-entry">
            <span className="hour-day">MONDAY - FRIDAY</span>
            <span className="hour-time">9 AM - 5 PM (IST)</span>
          </div>
          <div className="hour-entry">
            <span className="hour-day">SATURDAY</span>
            <span className="hour-time">10 AM - 2 PM (IST)</span>
          </div>
          <div className="hour-entry closed-entry">
            <span className="hour-day">SUNDAY</span>
            <span className="hour-time">CLOSED</span>
          </div>
        </div>
      </div>

      <div className="footer-bottom-bar-structured">
        <div className="copyright-info-structured">
          &copy; {new Date().getFullYear()} Nutrition Trends. All rights reserved.
        </div>
        <div className="social-links-structured">
          <a href="#" aria-label="Instagram">INSTAGRAM</a>
          <a href="#" aria-label="LinkedIn">LINKEDIN</a>
          <a href="#" aria-label="Twitter">TWITTER</a>
        </div>
      </div>
      
      {/* The large logo graphic at the very bottom, similar to the image */}
      <div className="footer-large-logo">
        NUTRITION TRENDS
      </div>

    </footer>
  );
};

const HomePage = () => {
  return (
    <div className="homepage-new-layout">
      <PageHeaderMinimal />

      <section className="hero-section-clean">
        <div className="hero-content-clean">
          <h1 className="main-title-clean">
            FROM MEAL LOGS
            <br />
            TO A CURATED PLAN
          </h1>
          <p className="tagline-clean">
            Organize, edit, and revisit your dietary plan in a space built for clarity. Forget manual
            tracking — create a visual structure that actually works.
          </p>
          <div className="cta-buttons-clean">
            <Link to="/signup" className="button primary-button large-button">
              Start Tracking Today
            </Link>
          </div>
        </div>
      </section>

      <FeatureCarousel />
      <AboutSection />
      <HowItWorksSection />
      
      <PillarsGrid />
      <StructuredFooter />
    </div>
  );
};

export default HomePage;
